import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { transformCodeToDate } from '@/functions/utils';
import { fetchCultures, fetchRecoltes } from '@/functions/fetch_tables';
import { useDataStore } from '@/stores/useDataStore';

// Define a specific type for reactor state data
interface ReactorState {
  id: string;
  date: string;
  pbr: string;
  productiviteBs: number;
  productiviteBsMain: number | null;
  specie_id: number;
}

export const useReactorStateStore = defineStore('reactorStateStore', () => {
  const isLoading = ref(false);
  const harvestYield = ref<ReactorState[]>([]);

  // Helper functions (unchanged)
  function daysBetween(date1: string, date2: string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  function findClosestVolRecolte(
    currentDaycode: string,
    prelevements: Prelevement[]
  ): number {
    const currentDate = transformCodeToDate(currentDaycode);
    const sortedPrelevements = prelevements
      .filter((p) => p.daycode !== currentDaycode)
      .sort((a, b) => a.daycode.localeCompare(b.daycode));

    for (const prelev of sortedPrelevements) {
      if (prelev.vol_recolte != null) {
        const prelevDate = transformCodeToDate(prelev.daycode);
        const daysDiff = Math.abs(daysBetween(prelevDate, currentDate));
        if (daysDiff === 1) {
          return prelev.vol_recolte;
        }
      }
    }
    return 0;
  }

  function caclulatePasDeTemps(culture: Culture, recolte: Recolte) {
    const date = transformCodeToDate(recolte.daycode);
    const recoltesCulture = culture.recoltes.sort((a, b) => a.daycode.localeCompare(b.daycode));
    const indexCurrent = recoltesCulture.findIndex(r => r.id === recolte.id);

    let pasDeTemps = 0;
    if (indexCurrent > 0) {
      const prevRecolte = recoltesCulture[indexCurrent - 1];
      const prevDate = transformCodeToDate(prevRecolte.daycode);
      pasDeTemps = daysBetween(prevDate, date);
    } else if (culture.startdaycode?.startday_code) {
      const startDate = transformCodeToDate(culture.startdaycode.startday_code);
      pasDeTemps = daysBetween(startDate, date);
    }
    return pasDeTemps;
  }

  function aggregateYieldData({
    recoltes,
    cultures,
  }: { recoltes: Recolte[]; cultures: Culture[] }): ReactorState[] {
    const yields: ReactorState[] = [];
    const culturesById: Record<number, Culture> = {};
    cultures.forEach(c => {
      culturesById[c.id] = c;
    });

    for (const recolte of recoltes) {
      const date = transformCodeToDate(recolte.daycode);
      for (const cultureRef of recolte.cultures) {
        const culture = culturesById[cultureRef.id];
        if (!culture) continue;

        const pbr = culture.pbr.numero_pbr;
        const specie_id = culture.specie.id;

        const prelevements = culture.prelevements.filter(
          (p) => p.daycode === recolte.daycode
        );
        if (prelevements.length === 0) continue;
        const prelevement = prelevements[0];

        let volumeTheoriquePBR = prelevement.vol_recolte ?? findClosestVolRecolte(recolte.daycode, culture.prelevements);

        const volumeTotalTheorique = recolte.cultures.reduce((acc, c) => {
          const cultureDetail = culturesById[c.id];
          if (!cultureDetail) return acc;
          const prelev = cultureDetail.prelevements.find(
            (pr) => pr.daycode === recolte.daycode
          );
          const volRecolte = prelev?.vol_recolte ?? findClosestVolRecolte(recolte.daycode, cultureDetail.prelevements);
          return acc + (volRecolte ?? 0);
        }, 0);

        const facteurVolume =
          volumeTotalTheorique > 0
            ? volumeTheoriquePBR / volumeTotalTheorique
            : 0;

        const volumeReelCentrifuge = recolte.volume ?? 0;
        const volumeCorrigePBR = facteurVolume * volumeReelCentrifuge;

        const pasDeTemps = caclulatePasDeTemps(culture, recolte);

        const msMesuree =
          prelevement.masse_residuelle && prelevement.vol_traite
            ? Number(((prelevement.masse_residuelle * 1000) / prelevement.vol_traite).toFixed(3))
            : 0;

        const msCalculee = prelevement.absorbance_880nm && culture?.specie?.ratio
          ? Number((prelevement.absorbance_880nm * (culture.specie.ratio ?? 0)).toFixed(3))
          : 0;

        const volumePbr = culture.pbr.volume;

        const productiviteBs =
          pasDeTemps > 0
            ? ((msMesuree * volumeCorrigePBR) / pasDeTemps / 1000 / (volumePbr / 1000)) * 292
            : 0;

        const productiviteBsMain =
          pasDeTemps > 0
            ? ((msCalculee * volumeCorrigePBR) / pasDeTemps / 1000 / (volumePbr / 1000)) * 292
            : 0;

        yields.push({
          id: `${recolte.id}_${culture.id}`,
          date,
          pbr,
          productiviteBs,
          productiviteBsMain,
          specie_id,
        });
      }
    }
    return yields;
  }

  async function loadData() {
    const dataStore = useDataStore();
    isLoading.value = true;
    try {
      const currentYear = new Date().getFullYear();
      await fetchCultures(true, currentYear, null, null);
      const activeCultureIds = dataStore.cultures.map(c => c.id);
      await fetchRecoltes(currentYear, null, null, activeCultureIds);
      harvestYield.value = aggregateYieldData({
        recoltes: dataStore.recoltes,
        cultures: dataStore.cultures,
      });
    } catch (error) {
      console.error('Error loading reactor state data:', error);
    } finally {
      isLoading.value = false;
    }
  }

  const filteredHarvestYield = computed(() => {
    return harvestYield.value
      .sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateA !== dateB
          ? dateA - dateB
          : a.pbr.localeCompare(b.pbr, undefined, { numeric: true, sensitivity: 'base' });
      });
  });

  return {
    isLoading,
    filteredHarvestYield,
    loadData,
  };
});